import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  const navLinks = [
    { label: "首页", to: "/" },
    { label: "关于我们", to: "/about" },
    { label: "联系我们", to: "/contact" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-background-200"
          : "bg-background-50 border-background-100"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <span className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 group-hover:bg-primary-600 transition-colors">
              <i className="ri-play-fill text-2xl leading-none" />
            </span>
            <span className="font-heading text-lg md:text-xl font-extrabold tracking-tight text-foreground-950 whitespace-nowrap">
              黄色免费视频
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-4">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname === "/"
                  ? "text-primary-600 bg-primary-50"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
              }`}
            >
              首页
            </Link>
            <div className="relative" ref={categoryRef}>
              <button
                type="button"
                onClick={() => setCategoryOpen((v) => !v)}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                  location.pathname.startsWith("/category")
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                分类
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
                </span>
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full mt-1 w-[420px] p-3 bg-background-50 border border-background-200 rounded-lg animate-fade-in">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/category/${c.slug}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      >
                        <span className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center text-primary-500">
                          <i className={`${c.icon} text-base`} />
                        </span>
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/search"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname === "/search"
                  ? "text-primary-600 bg-primary-50"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
              }`}
            >
              搜索
            </Link>
            {navLinks.slice(1).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  location.pathname === l.to
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex items-center flex-1 max-w-md ml-auto">
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索视频标题或简介"
                className="w-full h-10 pl-9 pr-20 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
              <button
                type="submit"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-3 text-xs font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开菜单"
            className="lg:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 cursor-pointer"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-4 space-y-4">
            <form onSubmit={submitSearch} className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索视频标题或简介"
                className="w-full h-11 pl-9 pr-4 text-sm bg-background-100 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </form>
            <div className="flex flex-col gap-1">
              <Link to="/" className="px-3 py-3 text-sm font-medium text-foreground-800 rounded-md hover:bg-background-100">
                首页
              </Link>
              <Link to="/search" className="px-3 py-3 text-sm font-medium text-foreground-800 rounded-md hover:bg-background-100">
                搜索
              </Link>
              {navLinks.slice(1).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-3 py-3 text-sm font-medium text-foreground-800 rounded-md hover:bg-background-100"
                >
                  {l.label}
                </Link>
              ))}
            </div>
            <div>
              <p className="px-3 pb-2 text-xs font-semibold text-foreground-500 uppercase tracking-wide">
                视频分类
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-700 bg-background-100"
                  >
                    <span className="w-6 h-6 rounded-md bg-background-50 flex items-center justify-center text-primary-500">
                      <i className={`${c.icon} text-sm`} />
                    </span>
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}